import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { truncate, sortBy, eq, map, filter, find } from 'lodash';
import sortByChapter from 'sort-by-chapter';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import removeHtml from '../util/remove_html_markup';
import buildPageTitle from '../util/buildPageTitle';
import Loader from './loader';
import Homepage from './page/homepage';
import DrillDownPage from './page/drillDownPage';
import { PUBLISH_DATA } from '../graphqls';

export default function Public({ linkPrefix, branding }) {
  const { subdomain: subDomain, pageId = 'home' } = useParams();
  const {
    loading: loadingPublishData,
    data: {
      publishData: {
        generalSettings = {},
        generalSettings: { name = '', planName = '', customFields = [], tags = [] } = {},
        pages = {},
      } = {},
    } = {},
    error,
  } = useQuery(PUBLISH_DATA, {
    variables: { pageId, subDomain },
    onCompleted({ publishData: { generalSettings: gs, generalSettings: { pageTitle: orgTitle = '' } = {}, pages: completedPages = {} } = {} }) {
      if (!gs) {
        window.location.href = 'https://app.envisio.com/404';
      } else {
        document.title = buildPageTitle(removeHtml(completedPages[pageId]?.title || ''), orgTitle);
      }
    },
  });

  useEffect(() => {
    document.title = buildPageTitle(removeHtml(pages[pageId]?.title || ''), generalSettings?.pageTitle);
  }, [pageId, pages[pageId]?.title, generalSettings?.pageTitle]);

  const siData = pages[pageId] || {};
  const pageArray = JSON.parse(JSON.stringify(Object.values(pages)));
  const pageObject = {};
  let tree = [];
  const level1PageIds = map(filter(pageArray, ['parentPlanNodeId', '']), 'planNodeId');
  const level2PageIds = map(
    pageArray.filter(({ parentPlanNodeId }) => level1PageIds.includes(parentPlanNodeId)),
    'planNodeId',
  );
  const level3PageIds = map(
    pageArray.filter(({ parentPlanNodeId }) => level2PageIds.includes(parentPlanNodeId)),
    'planNodeId',
  );
  const level4PageIds = map(
    pageArray.filter(({ parentPlanNodeId }) => level3PageIds.includes(parentPlanNodeId)),
    'planNodeId',
  );

  pageArray.forEach((_, pageIndex) => {
    pageObject[pageArray[pageIndex].planNodeId] = pageIndex;
    pageArray[pageIndex].items = [];
    pageArray[pageIndex].measures = [];
  });
  pageArray.forEach(
    ({
      parentPlanNodeId,
      measureId,
      planNodeId,
      title,
      description = '',
      latestUpdate = '',
      additionalBottomText = '',
      additionalMiddleText = '',
      additionalUpperMiddleText = '',
      additionalText = '',
      numberWithLabel,
      position,
      customFieldRecords,
      tags: taggings,
    }) => {
      const customFieldRecordContents = (customFieldRecords || [])
        .map(({ customFieldId, content }) => {
          const currentField = find(customFields || [], { customFieldId }) || {};
          if (
            ['TEXT', 'RICH_TEXT', 'NUMBER', 'SINGLE_SELECT_PICKLIST', 'MULTI_SELECT_PICKLIST'].includes(
              (currentField?.fieldType || '').toUpperCase(),
            )
          ) {
            return [currentField?.fieldName || '', content || ''].join(' ');
          }

          return '';
        })
        .join(' ');
      const tagNames = (taggings || []).map((tagId) => find(tags, { tagId })?.tagName || '').join(' ');

      if (parentPlanNodeId) {
        if (pageArray[pageObject[parentPlanNodeId]]) {
          // for dirty data only, should remove
          let level = 0;

          if (level1PageIds.includes(planNodeId)) {
            level = 1;
          } else if (level2PageIds.includes(planNodeId)) {
            level = 2;
          } else if (level3PageIds.includes(planNodeId)) {
            level = 3;
          } else if (level4PageIds.includes(planNodeId)) {
            level = 4;
          }

          pageArray[pageObject[parentPlanNodeId]].items.push({
            id: planNodeId,
            title: title || truncate(removeHtml(description), { separator: ' ', length: 60 }),
            isMeasure: false,
            numberWithLabel,
            items: pageArray[pageObject[planNodeId]].items,
            measures: pageArray[pageObject[planNodeId]].measures,
            level,
            latestUpdate,
            additionalBottomText,
            additionalMiddleText,
            additionalUpperMiddleText,
            additionalText,
            description,
            customFields: customFieldRecordContents,
            tags: tagNames,
          });
        }
      } else if (measureId) {
        pageArray[pageObject[planNodeId]].measures.push({
          id: measureId,
          title,
          isMeasure: true,
          position,
          latestUpdate,
          additionalBottomText,
          additionalMiddleText,
          additionalUpperMiddleText,
          additionalText,
          description,
          customFields: customFieldRecordContents,
          tags: tagNames,
        });
      } else if (parentPlanNodeId === '') {
        tree.push({
          id: planNodeId,
          title: title || truncate(removeHtml(description), { separator: ' ', length: 60 }),
          level: 1,
          isMeasure: false,
          items: pageArray[pageObject[planNodeId]].items,
          measures: pageArray[pageObject[planNodeId]].measures,
          numberWithLabel,
          latestUpdate,
          additionalBottomText,
          additionalMiddleText,
          additionalUpperMiddleText,
          additionalText,
          description,
          customFields: customFieldRecordContents,
          tags: tagNames,
        });
      }
    },
  );

  tree = sortByChapter(tree, { attribute: 'numberWithLabel' }).map(({ items: strategies, measures: goalMeasures, ...goalAttributes }) => ({
    items: sortByChapter(strategies, { attribute: 'numberWithLabel' }).map(
      ({ items: activities, measures: strategyMeasures, ...strategyAttributes }) => ({
        items: sortByChapter(activities, { attribute: 'numberWithLabel' }).map(({ measures: activityMeasures, ...activityAttributes }) => ({
          measures: sortBy(activityMeasures, ['position']),
          ...activityAttributes,
        })),
        measures: sortBy(strategyMeasures, ['position']),
        ...strategyAttributes,
      }),
    ),
    measures: sortBy(goalMeasures, ['position']),
    ...goalAttributes,
  }));
  // building search source
  const searchList = [];

  tree.forEach(
    ({
      id,
      title,
      measures,
      items,
      latestUpdate,
      additionalBottomText,
      additionalMiddleText,
      additionalUpperMiddleText,
      additionalText,
      description,
      customFields,
    }) => {
      searchList.push({
        id,
        isMeasure: false,
        title,
        latestUpdate,
        additionalBottomText,
        additionalMiddleText,
        additionalUpperMiddleText,
        additionalText,
        description,
        customFields,
        tags,
      });
      measures.forEach(
        ({
          id: measureId,
          title: measureTitle,
          latestUpdate,
          additionalBottomText,
          additionalMiddleText,
          additionalUpperMiddleText,
          additionalText,
          description,
          customFields,
        }) => {
          searchList.push({
            id: measureId,
            isMeasure: true,
            title: measureTitle,
            latestUpdate,
            additionalBottomText,
            additionalMiddleText,
            additionalUpperMiddleText,
            additionalText,
            description,
            customFields,
            tags,
          });
        },
      );
      items.forEach(
        ({
          id: strategyId,
          title: strategyTitle,
          measures: strategyMeasures,
          items: activities,
          latestUpdate,
          additionalBottomText,
          additionalMiddleText,
          additionalUpperMiddleText,
          additionalText,
          description,
          customFields,
          tags,
        }) => {
          searchList.push({
            id: strategyId,
            isMeasure: false,
            title: strategyTitle,
            latestUpdate,
            additionalBottomText,
            additionalMiddleText,
            additionalUpperMiddleText,
            additionalText,
            description,
            customFields,
            tags,
          });
          strategyMeasures.forEach(
            ({
              id: measureId,
              title: measureTitle,
              latestUpdate,
              additionalBottomText,
              additionalMiddleText,
              additionalUpperMiddleText,
              additionalText,
              description,
              customFields,
              tags,
            }) => {
              searchList.push({
                id: measureId,
                isMeasure: true,
                title: measureTitle,
                latestUpdate,
                additionalBottomText,
                additionalMiddleText,
                additionalUpperMiddleText,
                additionalText,
                description,
                customFields,
                tags,
              });
            },
          );
          activities.forEach(
            ({
              id: activityId,
              title: activityTitle,
              measures: activityMeasures,
              latestUpdate,
              additionalBottomText,
              additionalMiddleText,
              additionalUpperMiddleText,
              additionalText,
              description,
              customFields,
              tags,
            }) => {
              searchList.push({
                id: activityId,
                isMeasure: false,
                title: activityTitle,
                latestUpdate,
                additionalBottomText,
                additionalMiddleText,
                additionalUpperMiddleText,
                additionalText,
                description,
                customFields,
                tags,
              });
              activityMeasures.forEach(
                ({
                  id: measureId,
                  title: measureTitle,
                  latestUpdate,
                  additionalBottomText,
                  additionalMiddleText,
                  additionalUpperMiddleText,
                  additionalText,
                  description,
                  customFields,
                  tags,
                }) => {
                  searchList.push({
                    id: measureId,
                    isMeasure: true,
                    title: measureTitle,
                    latestUpdate,
                    additionalBottomText,
                    additionalMiddleText,
                    additionalUpperMiddleText,
                    additionalText,
                    description,
                    customFields,
                    tags,
                  });
                },
              );
            },
          );
        },
      );
    },
  );

  const isMeasure = !!siData.measureId;
  let pageLevel = 1;

  if (eq(pageId, 'home')) {
    pageLevel = 0;
  } else if (siData.ancestor) {
    pageLevel = siData.ancestor.length + (siData.measureId ? 0 : 1);
  }

  if (error) {
    // window.location.href = 'https://app.envisio.com/404';
  }

  if (loadingPublishData) {
    return <Loader loadingMessage="We're preparing the content...this will only take a minute." />;
  }

  return (
    <div className={`${['page', pageId].join('-')} ${isMeasure ? 'measure-' : ''}level-${pageLevel}`}>
      {pageId === 'home' ? (
        <Homepage
          data={siData}
          settings={{ ...generalSettings }}
          linkPrefix={linkPrefix}
          branding={branding}
          navs={tree}
          isPublic
          name={name}
          planName={planName}
        />
      ) : (
        <DrillDownPage
          data={siData}
          ancestor={siData.ancestor}
          settings={{ ...generalSettings }}
          linkPrefix={linkPrefix}
          branding={branding}
          navs={tree}
          isPublic
          name={name}
          planName={planName}
        />
      )}
    </div>
  );
}

Public.propTypes = {
  linkPrefix: PropTypes.string.isRequired,
  branding: PropTypes.bool.isRequired,
};
